import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Avatar,
  Chip,
  Button,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Container,
  Paper,
  useTheme,
  alpha,
  LinearProgress,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Dashboard,
  Assignment,
  CalendarToday,
  Security,
  Assessment,
  Book,
  ChangeCircle,
  ArrowBack,
  MoreVert,
  Business,
  LocationOn,
  Build,
  CheckCircle,
  Schedule,
  Warning,
  TrendingUp,
  Factory as FactoryIcon,
  Timeline as TimelineIcon,
  Visibility as VisibilityIcon,
  Assignment as AssignmentIcon,
  Videocam as VideocamIcon
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import PlantInformation from './PlantInformation/PlantInformation';
import { sitesService, SiteDashboardData } from '../services/sitesService';
import { PlantOverview } from './SCADA/PlantOverview';

interface SiteDashboardProps {
  siteId: number;
  siteName: string;
  assetOwnerName: string;
  assetOwnerLogoUrl?: string;
  onBack: () => void;
  onChangeSite: () => void;
  onChangeAssetOwner?: () => void;
}

interface DashboardModule {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  route?: string;
  status: 'available' | 'coming_soon' | 'beta';
  badge?: string;
  stats?: {
    label: string;
    value: string | number;
    trend?: 'up' | 'down' | 'stable';
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`site-tabpanel-${index}`}
      aria-labelledby={`site-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

const SiteDashboard: React.FC<SiteDashboardProps> = ({
  siteId,
  siteName,
  assetOwnerName,
  assetOwnerLogoUrl,
  onBack,
  onChangeSite,
  onChangeAssetOwner
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dashboardData, setDashboardData] = useState<SiteDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentView, setCurrentView] = useState<'dashboard' | 'plant_info'>('dashboard');
  const [tabValue, setTabValue] = useState(0);

  // Determine if we should show change links
  const hasMultipleAssetOwners = React.useMemo(() => {
    const roles = user?.site_roles?.filter(r => r.is_active) || [];
    const ownerIds = Array.from(new Set(roles.map(r => r.asset_owner_id)));
    return ownerIds.length > 1;
  }, [user]);

  const hasMultipleSitesForCurrentOwner = React.useMemo(() => {
    const roles = user?.site_roles?.filter(r => r.is_active) || [];
    if (roles.length === 0) return false;
    // Infer current owner from any role matching this site or fall back to first
    const currentOwnerId = roles.find(r => r.site_name === siteName)?.asset_owner_id ?? roles[0].asset_owner_id;
    const sitesForOwner = roles.filter(r => r.asset_owner_id === currentOwnerId);
    return sitesForOwner.length > 1;
  }, [user, siteName]);

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await sitesService.getSiteDashboard(siteId);
        console.log('Dashboard data received:', data);
        setDashboardData(data);
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [siteId]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Dashboard modules configuration with real data
  const modules: DashboardModule[] = [
    {
      id: 'plant_info',
      title: 'Plant Information',
      description: 'Equipment status, KKS hierarchy, and technical specifications',
      icon: <Dashboard sx={{ fontSize: 48 }} />,
      color: 'primary',
      status: 'available',
      stats: {
        label: 'Equipment Items',
        value: dashboardData?.statistics?.total_equipment || 0,
        trend: 'stable'
      }
    },
    {
      id: 'maintenance',
      title: 'Maintenance Management',
      description: 'Task scheduling, package completion, and overdue management',
      icon: <Build sx={{ fontSize: 48 }} />,
      color: 'warning',
      route: '/maintenance',
      status: 'available',
      stats: {
        label: 'Tasks Due',
        value: dashboardData?.statistics?.overdue_maintenance || 0,
        trend: dashboardData?.statistics?.overdue_maintenance > 0 ? 'up' : 'down'
      }
    },
    {
      id: 'permits',
      title: 'Permit Management',
      description: 'Create, manage, and track work permits and isolations',
      icon: <Assignment sx={{ fontSize: 48 }} />,
      color: 'success',
      route: '/permits',
      status: 'available',
      stats: {
        label: 'Active Permits',
        value: dashboardData?.statistics?.active_permits || 0,
        trend: 'stable'
      }
    },
    {
      id: 'cameras',
      title: 'Camera Management',
      description: 'Live video monitoring, recording, and security surveillance',
      icon: <VideocamIcon sx={{ fontSize: 48 }} />,
      color: 'primary',
      route: '/cameras',
      status: 'available',
      stats: {
        label: 'Online Cameras',
        value: dashboardData?.statistics?.online_cameras || 0,
        trend: dashboardData?.statistics?.total_cameras > dashboardData?.statistics?.online_cameras ? 'down' : 'stable'
      }
    },
    {
      id: 'historian',
      title: 'SCADA Historian',
      description: 'Multi-tag trending, historical data analysis, and charting',
      icon: <TimelineIcon sx={{ fontSize: 48 }} />,
      color: 'info',
      route: '/historian',
      status: 'available',
      stats: {
        label: 'SCADA Tags',
        value: dashboardData?.total_tags || 0,
        trend: 'stable'
      }
    },
    {
      id: 'station_log',
      title: 'Station Log',
      description: 'Real-time operations log and event tracking',
      icon: <Book sx={{ fontSize: 48 }} />,
      color: 'info',
      route: '/station-log',
      status: 'available',
      stats: {
        label: 'Today\'s Entries',
        value: 0, // TODO: Add log entries count to statistics
        trend: 'stable'
      }
    },
    {
      id: 'planned_works',
      title: 'Planned Works',
      description: 'Work scheduling, calendar view, and maintenance planning',
      icon: <CalendarToday sx={{ fontSize: 48 }} />,
      color: 'info',
      status: 'coming_soon',
      stats: {
        label: 'Scheduled',
        value: '0 works', // TODO: Add scheduled works count to statistics
        trend: 'stable'
      }
    },
    {
      id: 'safety',
      title: 'Safety Management',
      description: 'Safety protocols, incident reporting, and risk assessments',
      icon: <Security sx={{ fontSize: 48 }} />,
      color: 'error',
      route: '/safety',
      status: 'beta',
      stats: {
        label: 'Open Hazards',
        value: 0, // TODO: Add safety hazards count to statistics
        trend: 'stable'
      }
    },
    {
      id: 'reports',
      title: 'Reports & Analytics',
      description: 'Performance metrics, compliance reports, and data analytics',
      icon: <Assessment sx={{ fontSize: 48 }} />,
      color: 'secondary',
      status: 'coming_soon',
      stats: {
        label: 'Reports',
        value: 0, // TODO: Add recent activity count to statistics
        trend: 'stable'
      }
    }
  ];

  const handleModuleClick = (module: DashboardModule) => {
    if (module.status === 'available' || module.status === 'beta') {
      if (module.id === 'plant_info') {
        setCurrentView('plant_info');
      } else if (module.route) {
        navigate(module.route);
      }
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleBackToDashboard = () => {
    setCurrentView('dashboard');
  };

  const getStatusChip = (status: DashboardModule['status']) => {
    switch (status) {
      case 'available':
        return <Chip label="Active" color="success" size="small" />;
      case 'beta':
        return <Chip label="Beta" color="warning" size="small" />;
      case 'coming_soon':
        return <Chip label="Coming Soon" color="default" size="small" />;
      default:
        return null;
    }
  };

  const getTrendIcon = (trend?: 'up' | 'down' | 'stable') => {
    switch (trend) {
      case 'up':
        return <TrendingUp sx={{ fontSize: 16, color: 'success.main' }} />;
      case 'down':
        return <TrendingUp sx={{ fontSize: 16, color: 'error.main', transform: 'rotate(180deg)' }} />;
      default:
        return <Schedule sx={{ fontSize: 16, color: 'text.secondary' }} />;
    }
  };

  // Debug logo URL
  console.log('SiteDashboard - Asset Owner Logo URL:', assetOwnerLogoUrl);

  // Render plant information view
  if (currentView === 'plant_info') {
    return (
      <Box>
        <AppBar position="static" sx={{ mb: 3, borderRadius: 2 }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleBackToDashboard}
              sx={{ mr: 2 }}
            >
              <ArrowBack />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Plant Information - {siteName}
            </Typography>
            <IconButton color="inherit" onClick={handleMenuOpen}>
              <MoreVert />
            </IconButton>
          </Toolbar>
        </AppBar>
        
        <PlantInformation 
          selectedSiteId={siteId} 
          siteName={siteName} 
        />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" disableGutters>
      {/* Welcome Section */}
      <Box sx={{ mb: 4 }}>
        <Paper 
          sx={{ 
            p: 4, 
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {/* Left side - Welcome text and actions */}
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
              <Typography 
                variant="h3" 
                component="h1" 
                gutterBottom 
                sx={{ 
                  fontWeight: 700,
                  color: '#ffffff',
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
                }}
              >
                Welcome to {siteName}
              </Typography>
              <Box sx={{ ml: 2, display: 'flex', gap: 1 }}>
                {hasMultipleSitesForCurrentOwner && (
                  <Button variant="outlined" size="small" color="inherit" onClick={onChangeSite}>
                    Change site
                  </Button>
                )}
                {hasMultipleAssetOwners && (
                  <Button variant="outlined" size="small" color="inherit" onClick={onChangeAssetOwner || onBack}>
                    Change asset owner
                  </Button>
                )}
              </Box>
            </Box>
            
            {/* Right side - Asset Owner Logo */}
            {(() => {
              const displayLogoUrl = assetOwnerLogoUrl 
                || (dashboardData as any)?.site_info?.asset_owner_logo_url 
                || dashboardData?.asset_owner?.logo 
                || undefined;
              return displayLogoUrl ? (
              <Box sx={{ 
                ml: 3, 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 120,
                maxWidth: 200,
                 bgcolor: alpha(theme.palette.common.white, 0.8),
                borderRadius: 3,
                 p: 1.5,
                 border: `1px solid ${alpha(theme.palette.common.white, 0.4)}`
              }}>
                <img
                  src={displayLogoUrl}
                  alt={`${assetOwnerName} Logo`}
                  style={{
                    maxHeight: '48px',
                    maxWidth: '100%',
                    objectFit: 'contain',
                    // Removed filter to show original logo colors
                    opacity: 1
                  }}
                />
              </Box>
              ) : null;
            })()}
          </Box>
          
          {/* Background decoration */}
          <Box
            sx={{
              position: 'absolute',
              right: -50,
              top: -50,
              width: 160,
              height: 160,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.common.white, 0.08),
              zIndex: 0,
            }}
          />
        </Paper>
      </Box>

      {/* Plant Overview - Primary Focus */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
          Live Plant Overview
        </Typography>
        <PlantOverview 
          siteId={siteId}
        />
      </Box>

      {/* Modules removed for cleaner layout */}

      {/* Quick Actions removed for cleaner layout */}

      {/* Footer welcome note removed for streamlined UI */}

      {/* SCADA Dashboard Tabs */}
      <Box sx={{ width: '100%', mt: 4 }}>
        <Card>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              aria-label="site dashboard tabs"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab 
                icon={<AssignmentIcon />} 
                label="Active Permits" 
                id="site-tab-0"
                aria-controls="site-tabpanel-0"
              />
              <Tab 
                icon={<FactoryIcon />} 
                label="Equipment" 
                id="site-tab-1"
                aria-controls="site-tabpanel-1"
              />
              <Tab 
                icon={<VisibilityIcon />} 
                label="Safety" 
                id="site-tab-2"
                aria-controls="site-tabpanel-2"
              />
            </Tabs>
          </Box>

          {/* Active Permits Tab */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Active Permits
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" color="primary">
                        {dashboardData?.statistics?.active_permits || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Active Permits
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Includes Access Permits, Work Authority, General Work
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" color="warning.main">
                        {dashboardData?.statistics?.planned_permits || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Permits Awaiting Issue
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Permits in 'planned' status ready for issuing
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              
              {/* Permit Type Breakdown */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Active Permits by Type
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined" sx={{ bgcolor: '#E8F5E8' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body2" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                            STAY
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#FF6B35', fontWeight: 'bold' }}>
                            LIVE
                          </Typography>
                          <Typography variant="body2" sx={{ ml: 0.5 }}>
                            Access Permits
                          </Typography>
                        </Box>
                        <Typography variant="h5" color="success.main">
                          {dashboardData?.permit_types?.access || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Digital StayLive Access Permits with intelligent isolation
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Work Authority
                        </Typography>
                        <Typography variant="h5" color="primary">
                          {dashboardData?.permit_types?.work_authority || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Traditional work authority permits
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          General Work
                        </Typography>
                        <Typography variant="h5" color="info.main">
                          {dashboardData?.permit_types?.general_work || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          General work permits
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>

              {/* Recent Permits */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Recent Permit Activity
                </Typography>
                {dashboardData?.recent_permits?.length > 0 ? (
                  <Grid container spacing={1}>
                    {dashboardData.recent_permits.slice(0, 3).map((permit: any) => (
                      <Grid item xs={12} key={permit.id}>
                        <Card variant="outlined" sx={{ mb: 1 }}>
                          <CardContent sx={{ py: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  {permit.work_control_number || permit.permit_number}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {permit.permit_type === 'access' ? 'StayLive Access Permit' : 
                                   permit.permit_type === 'work_authority' ? 'Work Authority' : 
                                   'General Work'} • {permit.work_location || permit.work_description?.substring(0, 30) + '...'}
                                </Typography>
                              </Box>
                              <Chip 
                                size="small" 
                                label={permit.status} 
                                color={permit.status === 'issued' ? 'success' : 
                                       permit.status === 'planned' ? 'warning' : 'default'}
                              />
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    No recent permit activity. <Button size="small" href="/permits/access/new" sx={{ ml: 1 }}>
                      Create StayLive Access Permit
                    </Button>
                  </Alert>
                )}</Box>
            </Box>
          </TabPanel>

          {/* Equipment Tab */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Equipment Overview
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" color="primary">
                        0
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Equipment
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" color="success.main">
                        0
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Operating Normally
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" color="warning.main">
                        0
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Requires Attention
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" color="error.main">
                        0
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Out of Service
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              <Box sx={{ mt: 2 }}>
                <Alert severity="info">
                  Equipment status will be automatically updated based on SCADA monitoring and maintenance records.
                </Alert>
              </Box>
            </Box>
          </TabPanel>

          {/* Safety Tab */}
          <TabPanel value={tabValue} index={2}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Safety Overview
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" color="primary">
                        0
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Safety Incidents (30 days)
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" color="success.main">
                        0
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Days Without Incident
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              <Box sx={{ mt: 2 }}>
                <Alert severity="info">
                  Safety metrics will integrate with permit activities and SCADA alarm events for comprehensive safety monitoring.
                </Alert>
              </Box>
            </Box>
          </TabPanel>
        </Card>
      </Box>
    </Container>
  );
};

export default SiteDashboard; 